import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Switch from '@mui/material/Switch'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'

import Dashboard from '@components/Dashboard'
import Notification from '@components/Notification'
import UserMenu from '@components/UserMenu'

import { accentColorConfig, useAccentColor } from '@hooks/useAccentColor'
import useDify from '@hooks/useDify'
import useThemeSync from '@hooks/useThemeSync'

import Login from './Login'

import logoDark from '../assets/logo_dark.png'
import logoLight from '../assets/logo_light.png'
import { Bot, Check, ChevronLeft, Pencil, Trash2, X } from 'lucide-react'

import { Agent } from '@typings/dify'
import { createAgent, deleteAgent, fetchAgents, updateAgent } from '@services/agent.service'
import i18n from '@i18n/i18n'
import { getToken, isAdmin } from '@config/auth.config'
import { setCurrentAccount } from '@services/api.service'

const AgentsManagement = () => {
    const { theme, setTheme } = useThemeSync()
    const [showDashboard, setShowDashboard] = useState(false)

    const [loading, setLoading] = useState(false)
    const [agentsList, setAgentsList] = useState<Agent[]>([])
    const [agentNameEn, setAgentNameEn] = useState('')
    const [agentNameFr, setAgentNameFr] = useState('')
    const [agentDescriptionEn, setAgentDescriptionEn] = useState('')
    const [agentDescriptionFr, setAgentDescriptionFr] = useState('')
    const [agentColor, setAgentColor] = useState('#000000')
    const [apiKey, setApiKey] = useState<string | undefined>('')
    const [selectedRoles, setSelectedRoles] = useState('')
    const [allowFiles, setAllowFiles] = useState(false)

    const { t } = useTranslation()
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language)
    const isAuthenticated = useIsAuthenticated()
    const { accounts } = useMsal()
    const account = useAccount(accounts[0] || {})
    const [accentColor, setAccentColor] = useAccentColor()
    const navigate = useNavigate()
    const [leftTitle, setLeftTitle] = useState(t('agents_management.add_agent'))
    const [currentAgent, setCurrentAgent] = useState<Agent | null>(null)
    const [open, setOpen] = useState(false)
    const fetchCalled = useRef(false); // Use a ref to track if fetchData has been called

    const [isUserAdmin, setIsUserAdmin] = useState(false);
    const [isUserInitialized, setIsUserInitialized] = useState(false)

    const resetForm = () => {
        setLeftTitle(t('agents_management.add_agent'))
        setCurrentAgent(null)

        setAgentNameEn('')
        setAgentNameFr('')
        setAgentDescriptionEn('')
        setAgentDescriptionFr('')
        setAgentColor('#000000')
        setApiKey('')
        setSelectedRoles('')
        setAllowFiles(false)
    }

    const handleSubmit = async (e: { preventDefault: () => void }) => {
        e.preventDefault()

        const rolesArray = selectedRoles.split(',').map(role => role.trim()).filter(role => role !== "");
    
        const newAgent: Agent = {
            translations: [
                { field: 'label', language: 'EN', value: agentNameEn },
                { field: 'label', language: 'FR', value: agentNameFr },
                { field: 'description', language: 'EN', value: agentDescriptionEn },
                { field: 'description', language: 'FR', value: agentDescriptionFr },
            ],
            color: agentColor,
            apiKey: apiKey!,
            idGroupEntra: rolesArray,
            supportsFileUpload: allowFiles,

            id: 0,
            isActive: false,
            isFavorite: false,
            messageCount: 0,
            likeCount: 0,
            lastUsed: new Date(),
            conversations: [],
            stats: {
                messageCount: 0,
                likeCount: 0,
                lastUsed: new Date().toISOString()
            }
        }

        const createdAgent = await createAgent(newAgent)
        if (createdAgent) {
            fetchData()
        }
        resetForm()
    }

    const handleEdit = (agent: Agent) => {
        setLeftTitle(t('agents_management.modify_agent'))
        setCurrentAgent(agent)

        const agentLabelEn = agent.translations.find(
            (translation) => translation.field === 'label' && translation.language === "EN"
        )?.value
        const agentLabelFr = agent.translations.find(
            (translation) => translation.field === 'label' && translation.language === "FR"
        )?.value
        const agentDescriptionEn = agent.translations.find(
            (translation) => translation.field === 'description' && translation.language === "EN"
        )?.value
        const agentDescriptionFr = agent.translations.find(
            (translation) => translation.field === 'description' && translation.language === "FR"
        )?.value

        setAgentNameEn(agentLabelEn || '') 
        setAgentNameFr(agentLabelFr || '')
        setAgentDescriptionEn(agentDescriptionEn || '')
        setAgentDescriptionFr(agentDescriptionFr || '')
        setAgentColor(agent.color)
        setApiKey(agent.apiKey)
        setSelectedRoles(agent.idGroupEntra.join(', '))
        setAllowFiles(agent.supportsFileUpload)
    }

    const handleSubmitEdit = async (e: { preventDefault: () => void }) => {
        e.preventDefault()

        const rolesArray = selectedRoles.split(',').map(role => role.trim()).filter(role => role !== "");

        const updatedAgent: Agent = {
            translations: [
                { field: 'label', language: 'EN', value: agentNameEn },
                { field: 'label', language: 'FR', value: agentNameFr },
                { field: 'description', language: 'EN', value: agentDescriptionEn },
                { field: 'description', language: 'FR', value: agentDescriptionFr },
            ],
            color: agentColor,
            apiKey: apiKey!,
            idGroupEntra: rolesArray,
            supportsFileUpload: allowFiles,

            id: currentAgent!.id,
            isActive: currentAgent!.isActive,
            isFavorite: currentAgent!.isFavorite,
            messageCount: currentAgent!.messageCount,
            likeCount: currentAgent!.likeCount,
            lastUsed: new Date(),
            conversations: [],
            stats: {
                messageCount: 0,
                likeCount: 0,
                lastUsed: new Date().toISOString()
            }
        }

        const updated = await updateAgent(updatedAgent)

        if (updated) {
            fetchData()
        }
        resetForm()
    }

    const handleDeleteClick = (agent: Agent) => {
        setCurrentAgent(agent)
        setOpen(true)
    }

    const handleConfirmDelete = async () => {
        const deletedAgent = await deleteAgent(currentAgent!)
        if (deletedAgent) {
            fetchData()
        }
        setOpen(false)
        setCurrentAgent(null)
    }

    const handleToggleIsActive = async (agent: Agent) => {
        const updatedAgent: Agent = {
            ...agent,
            isActive: !agent.isActive,
            lastUsed: new Date(),
            stats: {
                ...agent.stats,
                lastUsed: new Date().toISOString()
            }
        }

        const updated = await updateAgent(updatedAgent)
        if (updated) {
            fetchData()
        }
    };

    const {
        agents,
        setUser,
        error,
        setError,
    } = useDify(agentsList, setAgentsList)

    const initializeUser = async () => {
        try {
            setCurrentAccount(account);
    
            // Vérifier si l'utilisateur est admin
            if (account) {
            const adminStatus = await isAdmin(account);
            
            setIsUserAdmin(adminStatus);
            }
        } catch (error) {
            console.error("Error fetching token:", error);
        } finally {
            setIsUserInitialized(true)
        }
    }

    const ensureValidAccentColor = () => {
        if (!['lightBlue', 'navyBlue', 'green', 'yellow', 'limeGreen'].includes(accentColor)) {
            setAccentColor('lightBlue')
        }
    }

    useEffect(() => {
        initializeUser();
      }, [account, setUser]);

    useEffect(ensureValidAccentColor, [accentColor, setAccentColor])

    // Update currentLanguage
    useEffect(() => {
        const handleLanguageChange = () => {
          setCurrentLanguage(i18n.language)
        }
        i18n.on('languageChanged', handleLanguageChange)
        return () => {
          i18n.off('languageChanged', handleLanguageChange)
        }
      }, [])

    useEffect(() => {
        if (currentAgent) {
            setLeftTitle(t('agents_management.modify_agent'))
        } else {
            setLeftTitle(t('agents_management.add_agent'))
        }
    }, [t, i18n.language])

    const fetchData = async () => {
        const result = await fetchAgents()

        const validatedAgents = result.map((agent: { id: any; isActive: any; apiKey: any; color: any; isFavorite: any; supportsFileUpload: any; messageCount: any; likeCount: any; lastUsed: any; idGroupEntra: any; translations: any; conversations: any; stats: { messageCount: any; likeCount: any; lastUsed: any } }) => ({
            id: agent.id || 0,
            isActive: agent.isActive,
            apiKey: agent.apiKey || '',
            color: agent.color || '',
            isFavorite: agent.isFavorite || false,
            supportsFileUpload: agent.supportsFileUpload || false,
            messageCount: agent.messageCount || 0,
            likeCount: agent.likeCount || 0,
            lastUsed: agent.lastUsed || new Date().toISOString(),
            idGroupEntra: agent.idGroupEntra || [],
            translations: agent.translations || [],
            conversations: agent.conversations || [],
            stats: {
                messageCount: agent.stats?.messageCount || 0,
                likeCount: agent.stats?.likeCount || 0,
                lastUsed: agent.stats?.lastUsed || ''
            }
        }))

        setAgentsList(validatedAgents)
    }

    // Fetch agents
    useEffect(() => {
        // Pour éviter d'être appelé deux fois de suite au chargement de la page
        if (!fetchCalled.current) {
            fetchCalled.current = true;
            setLoading(true);
            fetchData().then(() => {
                setLoading(false);
            });
        }
    }, []);

    return (
    <>
        {loading ? (
            <div className="h-screen flex bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
            </div>
        ) : isAuthenticated ? (
            <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900 transition-colors">
                <div className="flex-1 flex flex-col transition-all">
                    { /* Header */ }
                    <div className="h-16 border-b dark:border-gray-700 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-2 h-16 border-r dark:border-gray-700 px-4">
                                <Bot size={20} className={`${accentColorConfig[accentColor].text} dark:text-white`} />
                                <span className="text-xl font-normal text-gray-900 dark:text-white flex items-center">
                                    <img srcSet={theme === 'dark' ? logoDark : logoLight} alt="GPT Invivo" className="h-7" /> GPT
                                    <small
                                        className={`text-xs font-semibold ${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'} px-1 rounded-sm ml-1 mt-1`}
                                    >
                                        V2
                                    </small>
                                </span>
                            </div>
                            <h1 className="text-xl font-bold dark:text-white ml-4 px-4">
                                {t('agents_management.agents_management')}
                            </h1>
                        </div>
                        <UserMenu
                            theme={theme}
                            setTheme={setTheme}
                            onShowDashboard={() => setShowDashboard(true)}
                            account={account}
                            accentColor={accentColor}
                            setAccentColor={setAccentColor}
                            agents={agents}
                            showAgentsManagement={isUserAdmin}
                        />
                    </div>

                    { /* Title and Back */ }
                    <div className='pt-2 pl-2 border-b dark:border-gray-700'>
                        <button 
                            className='flex items-center justify-between font-bold dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 p-2 pr-4 rounded-full'
                            onClick={() => navigate("/")}
                        >
                            <ChevronLeft />
                            {t('agents_management.back')}
                        </button>

                        <div className="mb-6 -mt-6 flex justify-center">
                            <h2 className="text-2xl font-bold dark:text-white text-center">
                                {t('agents_management.welcome')}
                            </h2>
                        </div>
                    </div>

                    { /* Form and List */ }
                    <div className='flex h-auto w-full'>
                        { /* Add/Modify agent */ }
                        <div className='border-r dark:border-gray-700 flex-1 pt-6 pl-8'>
                            <h2 className="text-2xl font-bold dark:text-white mb-8">
                                {leftTitle}
                            </h2>
                            <form id="agentForm" onSubmit={currentAgent == null ? handleSubmit : handleSubmitEdit} className='flex flex-col'>
                                { /* Name */ }
                                <input 
                                    type="text" 
                                    name="agentNameEn" 
                                    placeholder={t('agents_management.agent_name_en') + " *"}
                                    value={agentNameEn}
                                    onChange={(e) => setAgentNameEn(e.target.value)}
                                    className='rounded-full dark:bg-gray-800 border dark:border-gray-600 dark:text-white pl-3 pb-1 w-1/3 mb-4'
                                    required
                                />
                                <input 
                                    type="text" 
                                    name="agentNameFr" 
                                    placeholder={t('agents_management.agent_name_fr') + " *"}
                                    value={agentNameFr}
                                    onChange={(e) => setAgentNameFr(e.target.value)}
                                    className='rounded-full dark:bg-gray-800 border dark:border-gray-600 dark:text-white pl-3 pb-1 w-1/3 mb-4'
                                    required
                                />

                                { /* Description */ }
                                <textarea
                                    name="agentDescriptionEn" 
                                    placeholder={t('agents_management.agent_description_en')}
                                    value={agentDescriptionEn}
                                    onChange={(e) => setAgentDescriptionEn(e.target.value)}
                                    className='rounded-lg dark:bg-gray-800 border dark:border-gray-600 dark:text-white pl-3 pb-1 w-3/4 mb-4'
                                />
                                <textarea
                                    name="agentDescriptionFr" 
                                    placeholder={t('agents_management.agent_description_fr')}
                                    value={agentDescriptionFr}
                                    onChange={(e) => setAgentDescriptionFr(e.target.value)}
                                    className='rounded-lg dark:bg-gray-800 border dark:border-gray-600 dark:text-white pl-3 pb-1 w-3/4 mb-4'
                                />

                                { /* Color */ }
                                <div className='flex items-center mb-6 ml-2'>
                                    <label className="dark:text-white mr-4">
                                        {t('agents_management.agent_color')}
                                    </label>
                                    <input 
                                        type="color" 
                                        name="agentColor"
                                        value={agentColor}
                                        onChange={(e) => setAgentColor(e.target.value)}
                                        className='w-8 h-8 bg-transparent'
                                    />
                                </div>

                                { /* Api key */ }
                                <input 
                                    type="text" 
                                    name="apiKey" 
                                    placeholder={t('agents_management.api_key') + " *"}
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    className='rounded-full dark:bg-gray-800 border dark:border-gray-600 dark:text-white pl-3 pb-1 w-1/3 mb-4'
                                    required
                                />

                                { /* Permissions */ }
                                <input
                                    type="text" 
                                    name="agentRoles"
                                    placeholder={t('agents_management.agent_permissions')}
                                    value={selectedRoles}
                                    onChange={(e) => setSelectedRoles(e.target.value)}
                                    className="rounded-full dark:bg-gray-800 border dark:border-gray-600 dark:text-white pl-3 pb-1 w-2/3 mb-4"
                                />

                                { /* Allow files */ }
                                <div className='flex items-center mb-2 ml-2'>
                                    <label className="dark:text-white mr-4">
                                        {t('agents_management.files')}
                                    </label>
                                    <Switch 
                                        className='mt-1'
                                        checked={allowFiles}
                                        onChange={(e: { target: { checked: boolean | ((prevState: boolean) => boolean) } }) => setAllowFiles(e.target.checked)}
                                        sx={{
                                            "& .MuiSwitch-thumb": {
                                                backgroundColor: theme == "dark" ? "white" : "black",
                                            },
                                            "& .MuiSwitch-track": {
                                                backgroundColor: "gray",
                                            },
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                backgroundColor: accentColorConfig[accentColor].hexa,
                                            },
                                        }}
                                    />
                                </div>

                                <label className="text-xs text-gray-500">
                                    {t('agents_management.required_field')}
                                </label>

                                { /* Preview */ }
                                <div className='flex flex-col items-center mt-4'>
                                    <h3 className="text-xl font-bold dark:text-white">
                                        {t('agents_management.preview')}
                                    </h3>
                                    <div className="flex items-center mt-4 space-x-4">
                                        <div 
                                            className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                                            style={{ backgroundColor: agentColor, aspectRatio: '1' }}
                                        >
                                            <span className="text-2xl font-bold">
                                                {currentLanguage == "en" ? agentNameEn && agentNameEn[0].toUpperCase() : agentNameFr && agentNameFr[0].toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="flex flex-col ml-4">
                                            <h4 className="text-xl font-bold" style={{ color: theme === 'dark' ? 'white' : 'black' }}>
                                                {currentLanguage == "en" ? agentNameEn : agentNameFr}
                                            </h4>
                                            <p className="text-gray-500 pr-6" style={{ color: theme === 'dark' ? '#d1d5db' : '#4b5563' }}>
                                                {currentLanguage == "en" ? agentDescriptionEn : agentDescriptionFr}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Submit */}
                                <div className="flex space-x-4 mt-8 justify-end mr-6 mb-4">
                                    <button 
                                        type="submit" 
                                        className="bg-green-500 text-white p-2 rounded-full hover:bg-green-700"
                                    >
                                        <Check />
                                    </button>           
                                    <button 
                                        type="button" 
                                        className="bg-red-600 text-white p-2 rounded-full hover:bg-red-800"
                                        onClick={resetForm}
                                    >
                                        <X />
                                    </button>
                                </div>

                            </form>
                        </div>

                        { /* Agents list */ }
                        <div className='flex-1 pt-6 pl-8'>
                            <h2 className="text-2xl font-bold dark:text-white mb-6">
                                {t('agents_management.managing_existing_agent')}
                            </h2>

                            <div className="space-y-4">
                                {agentsList.length === 0 ? (
                                    <p className="text-gray-500 text-center">{t('agents_management.no_agents_registered')}</p>
                                ) : ( agentsList.map((agent) => (
                                    <div key={agent.id} className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            {/* Switch Button */}
                                            <Switch 
                                                checked={agent.isActive}
                                                onChange={() => handleToggleIsActive(agent)}
                                                sx={{
                                                    "& .MuiSwitch-thumb": {
                                                        backgroundColor: theme == "dark" ? "white" : "black",
                                                    },
                                                    "& .MuiSwitch-track": {
                                                        backgroundColor: "gray",
                                                    },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                        backgroundColor: accentColorConfig[accentColor].hexa,
                                                    },
                                                }}
                                            />

                                            {/* Edit Button */}
                                            <button 
                                                onClick={() => handleEdit(agent)} 
                                                className={`p-2 rounded-full ${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].hover}`}
                                            >
                                                <Pencil className='h-4 w-4'/>
                                            </button>

                                            {/* Delete Button */}
                                            <button 
                                                onClick={() => handleDeleteClick(agent)}
                                                className="p-2 rounded-full bg-red-600 hover:bg-red-800 text-white"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>

                                            {/* Agent's Avatar and Info */}
                                            <div 
                                                className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                                                style={{ backgroundColor: agent.color, aspectRatio: '1' }}
                                            >
                                                <span className="text-xl font-bold">
                                                    {agent.translations.find(
                                                        (translation) => translation.field === 'label' && translation.language === currentLanguage.toUpperCase()
                                                    )?.value[0]?.toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex flex-col ml-4 pr-2">
                                                <h4 className="text-l font-bold" style={{ color: theme === 'dark' ? 'white' : 'black' }}>
                                                    {agent.translations.find(
                                                        (translation) => translation.field === 'label' && translation.language === currentLanguage.toUpperCase()
                                                    )?.value}
                                                </h4>
                                                <p className="text-xs text-gray-500">
                                                    {agent.translations.find(
                                                        (translation) => translation.field === 'description' && translation.language === currentLanguage.toUpperCase()
                                                    )?.value}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )))}
                            </div>
                        </div>
                    </div>

                </div>
                <Dashboard
                    agents={agents}
                    accentColor={accentColor}
                    isOpen={showDashboard}
                    onClose={() => setShowDashboard(false)}
                />
                <Notification message={error} onClose={() => setError(null)} />

                {/* Delete Confirmation Modal */}
                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>Suppression de l'agent</DialogTitle>
                    <DialogContent>
                        Voulez-vous supprimer l'agent <strong>{currentAgent?.translations.find(
                            (translation) => translation.field === 'label' && translation.language === currentLanguage.toUpperCase()
                        )?.value}</strong> ?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)} color="primary">Non</Button>
                        <Button onClick={handleConfirmDelete} color="error">Oui</Button>
                    </DialogActions>
                </Dialog>
            </div>
        ) : (
            <Login />
        )}
    </>
    )
}

export default AgentsManagement
